import React, { useEffect, useState } from "react";
import { Sparkles, ListPlus, X } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { Card, PrimaryButton } from "../UI";
import { ChoiceSelect } from "../ChoiceSelect";
import portalApi, { errMsg } from "../../lib/portalApi";

export const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export const TIME_PREFS = [
  { value: "any", label: "Any time" }, { value: "morning", label: "Mornings (before 12)" },
  { value: "afternoon", label: "Afternoons (12–5)" }, { value: "evening", label: "Evenings (after 5)" },
];

export const OffersBanner = ({ onClaimed }) => {
  const [offers, setOffers] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = () => portalApi.get("/portal/offers").then(({ data }) => setOffers(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const claim = async (id) => {
    if (busy) return;
    setBusy(id);
    try { await portalApi.post(`/portal/offers/${id}/claim`); toast.success("It's yours — session confirmed"); load(); onClaimed?.(); }
    catch (e) { toast.error(errMsg(e)); load(); } finally { setBusy(null); }
  };
  if (!offers.length) return null;
  return (
    <div className="space-y-2 mb-5" data-testid="portal-offers">
      {offers.map((o) => (
        <div key={o.id} className="p-4 rounded-2xl flex flex-col sm:flex-row sm:items-center gap-3 mf-fade-in" style={{ background: "rgba(201,168,76,0.16)" }} data-testid={`offer-${o.id}`}>
          <Sparkles className="w-5 h-5 shrink-0" style={{ color: "#8a6f20" }} />
          <div className="flex-1">
            <div className="text-sm font-medium">An earlier time opened up</div>
            <div className="text-sm" style={{ color: "var(--mf-text-muted)" }}>{format(new Date(o.start_time), "EEE, MMM d · h:mm a")} (clinician's local time) · first come, first served</div>
          </div>
          <PrimaryButton onClick={() => claim(o.id)} disabled={!!busy} testid={`offer-claim-${o.id}`}>{busy === o.id ? "Claiming…" : "Claim this time"}</PrimaryButton>
        </div>
      ))}
    </div>
  );
};

export const WaitlistCard = () => {
  const [entry, setEntry] = useState(null);
  const [form, setForm] = useState({ days: [1, 2, 3, 4, 5], time_pref: "any", notes: "" });
  const load = () => portalApi.get("/portal/waitlist").then(({ data }) => {
    setEntry(data.id ? data : false);
    if (data.id) setForm({ days: data.days, time_pref: data.time_pref, notes: data.notes || "" });
  });
  useEffect(() => { load(); }, []);
  const toggle = (d) => setForm({ ...form, days: form.days.includes(d) ? form.days.filter((x) => x !== d) : [...form.days, d] });
  const save = async () => {
    try { await portalApi.post("/portal/waitlist", form); toast.success(entry ? "Waitlist preferences updated" : "You're on the waitlist — we'll email you if a time opens"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const leave = async () => { await portalApi.delete("/portal/waitlist"); toast.success("Removed from waitlist"); load(); };
  if (entry === null) return null;
  return (
    <Card className="mt-5" testid="portal-waitlist">
      <h3 className="font-serif text-xl mb-1 flex items-center gap-2"><ListPlus className="w-5 h-5" style={{ color: "var(--mf-accent)" }} /> {entry ? "You're on the waitlist" : "Want an earlier time?"}</h3>
      <p className="text-sm mb-4" style={{ color: "var(--mf-text-muted)" }}>When someone cancels at a time that fits you, we'll email you right away. First to claim it gets it.</p>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {DAY_LABELS.map((d, i) => (
          <button key={d} onClick={() => toggle(i)} className="w-11 py-1.5 rounded-full text-xs font-medium" style={{ background: form.days.includes(i) ? "var(--mf-primary)" : "var(--mf-muted)", color: form.days.includes(i) ? "#FAFAF8" : "var(--mf-text)" }} data-testid={`waitlist-day-${i}`}>{d}</button>
        ))}
      </div>
      <div className="max-w-xs mb-4"><ChoiceSelect value={form.time_pref} onChange={(v) => setForm({ ...form, time_pref: v })} options={TIME_PREFS} testid="waitlist-time" /></div>
      <div className="flex flex-wrap gap-2">
        <PrimaryButton onClick={save} disabled={!form.days.length} testid="waitlist-save">{entry ? "Update" : "Join waitlist"}</PrimaryButton>
        {entry && <button onClick={leave} className="text-sm px-4 py-2 rounded-full flex items-center gap-1" style={{ background: "var(--mf-muted)" }} data-testid="waitlist-leave"><X className="w-4 h-4" /> Leave waitlist</button>}
      </div>
    </Card>
  );
};
